import { db, auth } from '../firebase-config';
import { 
  collection, 
  addDoc, 
  doc, 
  setDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp 
} from 'firebase/firestore';

class SelectedProfilesService {
  constructor() {
    this.auth = auth;
    this.db = db;
  }

  /**
   * Get current user ID
   * @returns {string} User ID
   */
  getCurrentUserId() {
    const user = this.auth.currentUser;
    if (!user) {
      throw new Error('No user logged in');
    }
    return user.uid;
  }

  /**
   * Track profile selection for backend analytics
   * @param {Object} profile - The profile data to track
   * @param {string} searchQuery - The search query that generated this match
   * @param {Object} userData - User's resume data and context
   * @param {Object} analyticsData - Additional analytics data
   * @returns {Promise<string>} Success status
   */
  async trackProfileSelection(profile, searchQuery, userData = {}, analyticsData = {}) {
    try {
      const userId = this.getCurrentUserId();
      
      // Normalize the profile data
      const normalizedProfile = {
        ...profile,
        name: profile.name || profile.profile_name || `${profile.first_name ?? ''} ${profile.last_name ?? ''}` || 'Unnamed',
        position: profile.position || profile.profile_current_position || profile.title || '',
        current_company_name: profile.current_company_name || profile.company_name || profile.company || '',
        url: profile.profile_url || profile.url || profile.linkedin || '',
        email: profile.email || profile.likely_email_1 || profile.estimated_profile_email || '',
      };

      // Prepare analytics payload for backend
      const analyticsPayload = {
        userId: userId,
        userEmail: this.auth.currentUser.email,
        profile: normalizedProfile,
        searchQuery: searchQuery,
        userData: userData,
        selectedAt: serverTimestamp(),
        relevanceScore: profile.relevance_score || profile.match_score || 0,
        matchReasons: profile.reason?.Reason || [],
        analytics: {
          ...analyticsData,
          source: 'match_results',
          version: '1.0',
          timestamp: new Date().toISOString(),
          sessionId: analyticsData.sessionId || null,
          userAgent: navigator.userAgent,
          screenResolution: `${window.screen.width}x${window.screen.height}`,
          timeOnPage: analyticsData.timeOnPage || 0,
          selectionMethod: analyticsData.selectionMethod || 'card_click',
          viewMode: analyticsData.viewMode || 'list',
          profileRank: analyticsData.profileRank || 0,
          totalResults: analyticsData.totalResults || 0,
          userIntent: userData.intent || 'unknown',
          userSubscription: analyticsData.userSubscription || 'free',
          selectionContext: {
            wasEmailGenerated: analyticsData.wasEmailGenerated || false,
            wasProfileViewed: analyticsData.wasProfileViewed || false,
            wasLinkedInClicked: analyticsData.wasLinkedInClicked || false,
            timeFromSearchToSelection: analyticsData.timeFromSearchToSelection || 0,
            scrollPosition: analyticsData.scrollPosition || 0,
            selectedAfterEmailGeneration: analyticsData.selectedAfterEmailGeneration || false
          }
        }
      };

      // Store directly in Firestore
      const docRef = await addDoc(collection(this.db, 'selected_profiles'), analyticsPayload);
      
      console.log('Profile selection tracked for backend analytics:', docRef.id);
      return 'success';
    } catch (error) {
      console.error('Error tracking profile selection:', error);
      // Silent fail - don't show any errors to customers
      return 'error';
    }
  }

  /**
   * Track bulk profile selections
   * @param {Array} profiles - Array of profile objects
   * @param {string} searchQuery - The search query
   * @param {Object} userData - User context data
   * @param {Object} analyticsData - Analytics context
   * @returns {Promise<string>} Success status
   */
  async trackBulkSelections(profiles, searchQuery, userData = {}, analyticsData = {}) {
    try {
      const userId = this.getCurrentUserId();
      const savedIds = [];

      for (let i = 0; i < profiles.length; i++) {
        const profile = profiles[i];
        const profileAnalytics = {
          ...analyticsData,
          selectionMethod: 'bulk_select',
          bulkSelectionIndex: i,
          bulkSelectionTotal: profiles.length
        };
        
        const result = await this.trackProfileSelection(profile, searchQuery, userData, profileAnalytics);
        if (result === 'success') {
          savedIds.push(`profile_${i}`);
        }
      }

      console.log(`Tracked ${savedIds.length} bulk profile selections for analytics`);
      return 'success';
    } catch (error) {
      console.error('Error tracking bulk selections:', error);
      return 'error';
    }
  }

  /**
   * Track profile deselection
   * @param {Object} profile - The profile being deselected
   * @param {Object} analyticsData - Analytics context
   * @returns {Promise<string>} Success status
   */
  async trackProfileDeselection(profile, analyticsData = {}) {
    try {
      const userId = this.getCurrentUserId();
      
      const deselectionData = {
        userId: userId,
        userEmail: this.auth.currentUser.email,
        profile: profile,
        deselectedAt: serverTimestamp(),
        analytics: {
          ...analyticsData,
          action: 'deselection',
          source: 'match_results',
          version: '1.0',
          timestamp: new Date().toISOString()
        }
      };

      const docRef = await addDoc(collection(this.db, 'profile_deselections'), deselectionData);
      console.log('Profile deselection tracked for backend analytics:', docRef.id);
      return 'success';
    } catch (error) {
      console.error('Error tracking profile deselection:', error);
      return 'error';
    }
  }

  /**
   * Track session data
   * @param {Object} sessionData - Session information
   * @returns {Promise<string>} Success status
   */
  async trackSessionData(sessionData = {}) {
    try {
      const userId = this.getCurrentUserId();
      
      const sessionPayload = {
        userId: userId,
        userEmail: this.auth.currentUser.email,
        sessionStart: serverTimestamp(),
        ...sessionData,
        analytics: {
          userAgent: navigator.userAgent,
          screenResolution: `${window.screen.width}x${window.screen.height}`,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          language: navigator.language,
          referrer: document.referrer,
          url: window.location.href
        }
      };

      const docRef = await addDoc(collection(this.db, 'user_sessions'), sessionPayload);
      console.log('Session data tracked for backend analytics:', docRef.id);
      return 'success';
    } catch (error) {
      console.error('Error tracking session data:', error);
      return 'error';
    }
  }

  /**
   * Get analytics data (admin only - via backend script)
   * @param {number} limit - Maximum number of records to retrieve
   * @returns {Promise<Array>} Array of analytics records
   */
  async getAnalyticsData(limit = 100) {
    try {
      const userId = this.getCurrentUserId();
      
      const q = query(
        collection(this.db, 'selected_profiles'),
        where('userId', '==', userId),
        orderBy('selectedAt', 'desc'),
        limit(limit)
      );

      const querySnapshot = await getDocs(q);
      const analytics = [];

      querySnapshot.forEach((doc) => {
        analytics.push({
          id: doc.id,
          ...doc.data()
        });
      });

      return analytics;
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      throw error;
    }
  }
}

export default new SelectedProfilesService(); 